import type {
  TreeGeometryEvidenceIssue,
  TreeGeometryEvidenceReport,
} from '@/governor/treeGeometryEvidence';

import {
  LEGACY_ANCESTRY_HORIZONTAL_RULES,
  type LegacyAncestryHorizontalRule,
} from './TreeRuntimeLegacyAncestryResidueCausalLocalization';

import {
  TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,
  buildTreeRuntimeCanonicalBranchCounterfactualGeometryEvidence,
  type TreeRuntimeCanonicalBranchCounterfactualComparison,
  type TreeRuntimeCanonicalBranchCounterfactualLayout,
  type TreeRuntimeCanonicalBranchCounterfactualOptions,
  type TreeRuntimeCanonicalBranchCounterfactualRuleCounts,
  type TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
  type TreeRuntimeCanonicalBranchCounterfactualStructuralMember,
} from './TreeRuntimeCanonicalBranchCounterfactualShadowProjection';

import type {
  TreeRuntimeBranchLayoutAuthoritySnapshot,
} from './TreeRuntimeBranchLayoutAuthoritySnapshot';

import {
  TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_MATRIX_SCHEMA,
  isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent,
  isTreeRuntimeCanonicalBranchCounterfactualShadowPlanAuthorityCoherent,
  type TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
} from './TreeRuntimeBranchLayoutInteractionCounterfactualMatrix';

export const
TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA =
  'CC-TREE-LIVE-003G3E4' as const;

type PrivateFailureRecord = {
  readonly key: string;

  readonly issue:
    TreeGeometryEvidenceIssue;

  readonly indeterminate:
    boolean;
};

export interface TreeRuntimeBranchLayoutInteractionCellComparison {
  readonly availability:
    'OBSERVED' | 'INVALID_EVIDENCE';

  readonly shadowFatalCount:
    number;

  readonly resolvedFatalCount:
    number;

  readonly persistedFatalCount:
    number;

  readonly introducedFatalCount:
    number;

  readonly indeterminateControlFatalCount:
    number;

  readonly rootBoundary:
    TreeRuntimeCanonicalBranchCounterfactualRuleCounts;

  readonly horizontal:
    Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        TreeRuntimeCanonicalBranchCounterfactualRuleCounts
      >
    >;

  readonly horizontalControlCount:
    number;

  readonly horizontalResolvedCount:
    number;

  readonly horizontalPersistedCount:
    number;

  readonly horizontalIntroducedCount:
    number;

  readonly horizontalIndeterminateCount:
    number;

  readonly invalidJoinCount:
    number;
}

export interface TreeRuntimeBranchLayoutInteractionControlSummary {
  readonly fatalCount:
    number;

  readonly rootBoundaryCount:
    number;

  readonly horizontalCount:
    number;

  readonly horizontal:
    Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        number
      >
    >;
}

export interface TreeRuntimeBranchLayoutInteractionFourCellComparison {
  readonly schema:
    typeof TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA;

  readonly availability:
    'OBSERVED' | 'INVALID_EVIDENCE';

  readonly controlParity:
    boolean;

  readonly controlWorldParity:
    boolean;

  readonly controlFailureParity:
    boolean;

  readonly cellBReferenceCoherent:
    boolean;

  readonly cellA:
    TreeRuntimeBranchLayoutInteractionControlSummary;

  readonly cellB:
    TreeRuntimeCanonicalBranchCounterfactualComparison;

  readonly cellC:
    TreeRuntimeBranchLayoutInteractionCellComparison;

  readonly cellD:
    TreeRuntimeBranchLayoutInteractionCellComparison;

  readonly semanticSubstitutedMemberCount:
    number;

  readonly semanticIndeterminateMemberCount:
    number;

  readonly layoutSubstitutedMemberCount:
    number;

  readonly layoutIndeterminateMemberCount:
    number;

  readonly sourceMutationAllowed:
    false;

  readonly causalAttributionAllowed:
    false;

  readonly repairAuthorized:
    false;

  readonly legacyRetirementAuthorized:
    false;

  readonly productionAuthorityChanged:
    false;

  readonly privateIdentityPublished:
    false;

  readonly readOnly:
    true;

  readonly repairExecuted:
    false;
}

const privateIssueKey = (
  issue:
    TreeGeometryEvidenceIssue,
): string =>
  JSON.stringify([
    issue.rule,
    [...(issue.memberIds ?? [])]
      .sort(),
    [...(issue.edgeKeys ?? [])]
      .sort(),
    issue.routeRejectionStatus ??
      null,
  ]);

const fatalRecords = (
  report:
    TreeGeometryEvidenceReport,

  layout:
    TreeRuntimeCanonicalBranchCounterfactualLayout,

  indeterminateMemberIds:
    ReadonlySet<string>,
): PrivateFailureRecord[] => {
  const edgeMembers =
    new Map<
      string,
      Set<string>
    >();

  for (const edge of layout.edges) {
    if (!edge.key) {
      continue;
    }

    edgeMembers.set(
      edge.key,
      new Set([
        ...edge.fromMemberIds,
        edge.toMemberId,
      ]),
    );
  }

  return report.issues
    .filter(
      issue =>
        issue.status ===
          'FAIL',
    )
    .map(
      issue => {
        const touched =
          new Set<string>(
            issue.memberIds ?? [],
          );

        for (
          const edgeKey
          of issue.edgeKeys ?? []
        ) {
          for (
            const memberId
            of edgeMembers.get(
              edgeKey,
            ) ?? []
          ) {
            touched.add(
              memberId,
            );
          }
        }

        return {
          key:
            privateIssueKey(
              issue,
            ),

          issue,

          indeterminate:
            [...touched].some(
              memberId =>
                indeterminateMemberIds
                  .has(memberId),
            ),
        };
      },
    );
};

const partitionFailures = (
  control:
    readonly PrivateFailureRecord[],

  shadow:
    readonly PrivateFailureRecord[],
) => {
  const used =
    new Array(
      shadow.length,
    ).fill(false);

  const resolved:
    PrivateFailureRecord[] =
      [];

  const persisted:
    PrivateFailureRecord[] =
      [];

  for (const record of control) {
    const index =
      shadow.findIndex(
        (
          candidate,
          candidateIndex,
        ) =>
          !used[
            candidateIndex
          ] &&
          candidate.key ===
            record.key,
      );

    if (index < 0) {
      resolved.push(
        record,
      );

      continue;
    }

    used[index] =
      true;

    persisted.push(
      record,
    );
  }

  const introduced =
    shadow.filter(
      (
        _record,
        index,
      ) =>
        !used[index],
    );

  return {
    resolved,
    persisted,
    introduced,
  };
};

const rootBoundaryFailure = (
  record:
    PrivateFailureRecord,
): boolean =>
  record.issue.rule ===
    'GEOMETRY-CONNECTOR-ROUTE-REJECTED' &&
  record.issue
    .routeRejectionStatus ===
      'REJECTED-ROOT-BOUNDARY';

const horizontalRuleOf = (
  record:
    PrivateFailureRecord,
): LegacyAncestryHorizontalRule | null =>
  LEGACY_ANCESTRY_HORIZONTAL_RULES
    .includes(
      record.issue.rule as
        LegacyAncestryHorizontalRule,
    )
      ? record.issue.rule as
          LegacyAncestryHorizontalRule
      : null;

const countsFor = (
  control:
    readonly PrivateFailureRecord[],

  resolved:
    readonly PrivateFailureRecord[],

  persisted:
    readonly PrivateFailureRecord[],

  introduced:
    readonly PrivateFailureRecord[],

  predicate:
    (
      record:
        PrivateFailureRecord,
    ) => boolean,
): TreeRuntimeCanonicalBranchCounterfactualRuleCounts =>
  Object.freeze({
    control:
      control.filter(
        predicate,
      ).length,

    resolved:
      resolved.filter(
        predicate,
      ).length,

    persisted:
      persisted.filter(
        predicate,
      ).length,

    introduced:
      introduced.filter(
        predicate,
      ).length,

    indeterminate:
      control.filter(
        record =>
          predicate(record) &&
          record.indeterminate,
      ).length,
  });

const buildLayoutRulePaths = (
  authority:
    TreeRuntimeBranchLayoutAuthoritySnapshot,

  matrixPlan:
    TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
): ReadonlyMap<
  string,
  string
> => {
  const paths =
    new Map(
      authority
        .layoutPath
        .branchPathByMemberId,
    );

  for (
    const substitution
    of matrixPlan.layoutSubstitutions
  ) {
    paths.set(
      substitution.memberId,
      substitution.canonicalLayoutPath,
    );
  }

  return paths;
};

const buildCombinedRulePaths = (
  layoutPaths:
    ReadonlyMap<
      string,
      string
    >,

  semanticPlan:
    TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
): ReadonlyMap<
  string,
  string
> => {
  const paths =
    new Map(
      layoutPaths,
    );

  for (
    const [
      memberId,
      path,
    ]
    of semanticPlan
      .canonicalSemanticBranchPathByMemberId
  ) {
    paths.set(
      memberId,
      path,
    );
  }

  return paths;
};

const compareCell = (
  input: {
    readonly controlEvidence:
      TreeGeometryEvidenceReport;

    readonly controlLayout:
      TreeRuntimeCanonicalBranchCounterfactualLayout;

    readonly shadowLayout:
      TreeRuntimeCanonicalBranchCounterfactualLayout;

    readonly shadowRulePathByMemberId:
      ReadonlyMap<
        string,
        string
      >;

    readonly indeterminateMemberIds:
      ReadonlySet<string>;

    readonly structuralMembers:
      readonly TreeRuntimeCanonicalBranchCounterfactualStructuralMember[];

    readonly viewerMemberId:
      string | null;

    readonly centeredMemberId:
      string | null;

    readonly options:
      TreeRuntimeCanonicalBranchCounterfactualOptions;

    readonly controlParity:
      boolean;
  },
): TreeRuntimeBranchLayoutInteractionCellComparison => {
  const shadowEvidence =
    buildTreeRuntimeCanonicalBranchCounterfactualGeometryEvidence(
      input.shadowLayout,
      input.structuralMembers,
      input.shadowRulePathByMemberId,
      input.viewerMemberId,
      input.centeredMemberId,
      input.options,
    );

  const control =
    fatalRecords(
      input.controlEvidence,
      input.controlLayout,
      input.indeterminateMemberIds,
    );

  const shadow =
    fatalRecords(
      shadowEvidence,
      input.shadowLayout,
      input.indeterminateMemberIds,
    );

  const partition =
    partitionFailures(
      control,
      shadow,
    );

  const rootBoundary =
    countsFor(
      control,
      partition.resolved,
      partition.persisted,
      partition.introduced,
      rootBoundaryFailure,
    );

  const horizontal =
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES
        .map(
          rule => [
            rule,

            countsFor(
              control,
              partition.resolved,
              partition.persisted,
              partition.introduced,
              record =>
                horizontalRuleOf(
                  record,
                ) ===
                rule,
            ),
          ],
        ),
    ) as Record<
      LegacyAncestryHorizontalRule,
      TreeRuntimeCanonicalBranchCounterfactualRuleCounts
    >;

  const horizontalControlCount =
    Object.values(
      horizontal,
    ).reduce(
      (
        sum,
        value,
      ) =>
        sum +
        value.control,
      0,
    );

  const horizontalResolvedCount =
    Object.values(
      horizontal,
    ).reduce(
      (
        sum,
        value,
      ) =>
        sum +
        value.resolved,
      0,
    );

  const horizontalPersistedCount =
    Object.values(
      horizontal,
    ).reduce(
      (
        sum,
        value,
      ) =>
        sum +
        value.persisted,
      0,
    );

  const horizontalIntroducedCount =
    Object.values(
      horizontal,
    ).reduce(
      (
        sum,
        value,
      ) =>
        sum +
        value.introduced,
      0,
    );

  const horizontalIndeterminateCount =
    Object.values(
      horizontal,
    ).reduce(
      (
        sum,
        value,
      ) =>
        sum +
        value.indeterminate,
      0,
    );

  const arithmeticValid =
    partition.resolved.length +
      partition.persisted.length ===
        control.length &&

    partition.persisted.length +
      partition.introduced.length ===
        shadow.length;

  const invalidJoinCount =
    arithmeticValid
      ? 0
      : 1;

  return Object.freeze({
    availability:
      input.controlParity &&
      arithmeticValid
        ? 'OBSERVED'
        : 'INVALID_EVIDENCE',

    shadowFatalCount:
      shadow.length,

    resolvedFatalCount:
      partition.resolved.length,

    persistedFatalCount:
      partition.persisted.length,

    introducedFatalCount:
      partition.introduced.length,

    indeterminateControlFatalCount:
      control.filter(
        record =>
          record.indeterminate,
      ).length,

    rootBoundary:
      Object.freeze({
        ...rootBoundary,
      }),

    horizontal:
      Object.freeze({
        ...horizontal,
      }),

    horizontalControlCount,
    horizontalResolvedCount,
    horizontalPersistedCount,
    horizontalIntroducedCount,
    horizontalIndeterminateCount,

    invalidJoinCount,
  });
};

export const
compareTreeRuntimeBranchLayoutInteractionFourCellMatrix =
(
  input: {
    readonly authority:
      TreeRuntimeBranchLayoutAuthoritySnapshot;

    readonly matrixPlan:
      TreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan;

    readonly semanticPlan:
      TreeRuntimeCanonicalBranchCounterfactualShadowPlan;

    readonly cellBComparison:
      TreeRuntimeCanonicalBranchCounterfactualComparison;

    readonly controlLayout:
      TreeRuntimeCanonicalBranchCounterfactualLayout;

    readonly cellCLayout:
      TreeRuntimeCanonicalBranchCounterfactualLayout;

    readonly cellDLayout:
      TreeRuntimeCanonicalBranchCounterfactualLayout;

    readonly structuralMembers:
      readonly TreeRuntimeCanonicalBranchCounterfactualStructuralMember[];

    readonly productionGeometryEvidence:
      TreeGeometryEvidenceReport;

    readonly viewerMemberId:
      string | null;

    readonly centeredMemberId:
      string | null;

    readonly options:
      TreeRuntimeCanonicalBranchCounterfactualOptions;
  },
): TreeRuntimeBranchLayoutInteractionFourCellComparison => {
  const matrixCoherent =
    input.matrixPlan.schema ===
      TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_MATRIX_SCHEMA &&

    isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent(
      input.matrixPlan,
      input.authority,
    );

  const semanticCoherent =
    isTreeRuntimeCanonicalBranchCounterfactualShadowPlanAuthorityCoherent(
      input.semanticPlan,
      input.authority,
    ) &&

    input.semanticPlan
      .substitutedMemberCount ===
      input.matrixPlan
        .semanticSubstitutedMemberCount;

  const controlRulePaths =
    input.authority
      .layoutPath
      .branchPathByMemberId;

  const controlEvidence =
    buildTreeRuntimeCanonicalBranchCounterfactualGeometryEvidence(
      input.controlLayout,
      input.structuralMembers,
      controlRulePaths,
      input.viewerMemberId,
      input.centeredMemberId,
      input.options,
    );

  const productionFatalKeys =
    input.productionGeometryEvidence
      .issues
      .filter(
        issue =>
          issue.status ===
            'FAIL',
      )
      .map(
        privateIssueKey,
      )
      .sort();

  const controlFatalKeys =
    controlEvidence
      .issues
      .filter(
        issue =>
          issue.status ===
            'FAIL',
      )
      .map(
        privateIssueKey,
      )
      .sort();

  const controlFailureParity =
    JSON.stringify(
      productionFatalKeys,
    ) ===
    JSON.stringify(
      controlFatalKeys,
    );

  const controlWorldParity =
    input.productionGeometryEvidence
      .cardCount ===
        controlEvidence.cardCount &&

    input.productionGeometryEvidence
      .edgeCount ===
        controlEvidence.edgeCount &&

    input.productionGeometryEvidence
      .spouseLinkCount ===
        controlEvidence.spouseLinkCount &&

    input.productionGeometryEvidence
      .companionChipCount ===
        0;

  const controlParity =
    controlFailureParity &&
    controlWorldParity;

  const controlRecords =
    fatalRecords(
      controlEvidence,
      input.controlLayout,
      new Set(),
    );

  const cellARootBoundaryCount =
    controlRecords.filter(
      rootBoundaryFailure,
    ).length;

  const cellAHorizontal =
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES
        .map(
          rule => [
            rule,

            controlRecords.filter(
              record =>
                horizontalRuleOf(
                  record,
                ) ===
                rule,
            ).length,
          ],
        ),
    ) as Record<
      LegacyAncestryHorizontalRule,
      number
    >;

  const cellAHorizontalCount =
    Object.values(
      cellAHorizontal,
    ).reduce(
      (
        sum,
        value,
      ) =>
        sum +
        value,
      0,
    );

  const cellA =
    Object.freeze({
      fatalCount:
        controlRecords.length,

      rootBoundaryCount:
        cellARootBoundaryCount,

      horizontalCount:
        cellAHorizontalCount,

      horizontal:
        Object.freeze({
          ...cellAHorizontal,
        }),
    });

  const cellBReferenceCoherent =
    input.cellBComparison
      .availability ===
        'OBSERVED' &&

    input.cellBComparison
      .schema ===
        TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA &&

    input.cellBComparison
      .controlParity ===
        true &&

    input.cellBComparison
      .controlWorldParity ===
        true &&

    input.cellBComparison
      .controlFailureParity ===
        true &&

    input.cellBComparison
      .invalidJoinCount ===
        0 &&

    input.cellBComparison
      .controlFatalCount ===
        cellA.fatalCount &&

    input.cellBComparison
      .rootBoundary
      .control ===
        cellA.rootBoundaryCount &&

    input.cellBComparison
      .substitutedMemberCount ===
        input.semanticPlan
          .substitutedMemberCount &&

    input.cellBComparison
      .indeterminateMemberCount ===
        input.semanticPlan
          .indeterminateMemberCount &&

    LEGACY_ANCESTRY_HORIZONTAL_RULES
      .every(
        rule =>
          input.cellBComparison
            .horizontal[rule]
            .control ===
          cellA.horizontal[
            rule
          ],
      ) &&

    input.cellBComparison
      .sourceMutationAllowed ===
        false &&

    input.cellBComparison
      .causalAttributionAllowed ===
        false &&

    input.cellBComparison
      .legacyRetirementAuthorized ===
        false &&

    input.cellBComparison
      .productionAuthorityChanged ===
        false &&

    input.cellBComparison
      .privateIdentityPublished ===
        false &&

    input.cellBComparison
      .readOnly ===
        true &&

    input.cellBComparison
      .repairExecuted ===
        false;

  const layoutRulePaths =
    buildLayoutRulePaths(
      input.authority,
      input.matrixPlan,
    );

  const combinedRulePaths =
    buildCombinedRulePaths(
      layoutRulePaths,
      input.semanticPlan,
    );

  const cellCIndeterminate =
    new Set(
      input.matrixPlan
        .layoutIndeterminateMemberIds,
    );

  const cellDIndeterminate =
    new Set<string>([
      ...input.matrixPlan
        .layoutIndeterminateMemberIds,

      ...input.semanticPlan
        .indeterminateMemberIds,
    ]);

  const cellC =
    compareCell({
      controlEvidence,
      controlLayout:
        input.controlLayout,

      shadowLayout:
        input.cellCLayout,

      shadowRulePathByMemberId:
        layoutRulePaths,

      indeterminateMemberIds:
        cellCIndeterminate,

      structuralMembers:
        input.structuralMembers,

      viewerMemberId:
        input.viewerMemberId,

      centeredMemberId:
        input.centeredMemberId,

      options:
        input.options,

      controlParity,
    });

  const cellD =
    compareCell({
      controlEvidence,
      controlLayout:
        input.controlLayout,

      shadowLayout:
        input.cellDLayout,

      shadowRulePathByMemberId:
        combinedRulePaths,

      indeterminateMemberIds:
        cellDIndeterminate,

      structuralMembers:
        input.structuralMembers,

      viewerMemberId:
        input.viewerMemberId,

      centeredMemberId:
        input.centeredMemberId,

      options:
        input.options,

      controlParity,
    });

  const availability =
    matrixCoherent &&
    semanticCoherent &&
    controlParity &&
    cellBReferenceCoherent &&
    cellC.availability ===
      'OBSERVED' &&
    cellD.availability ===
      'OBSERVED'
      ? 'OBSERVED'
      : 'INVALID_EVIDENCE';

  return Object.freeze({
    schema:
      TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA,

    availability,

    controlParity,
    controlWorldParity,
    controlFailureParity,

    cellBReferenceCoherent,

    cellA,

    cellB:
      input.cellBComparison,

    cellC,

    cellD,

    semanticSubstitutedMemberCount:
      input.semanticPlan
        .substitutedMemberCount,

    semanticIndeterminateMemberCount:
      input.semanticPlan
        .indeterminateMemberCount,

    layoutSubstitutedMemberCount:
      input.matrixPlan
        .layoutSubstitutedMemberCount,

    layoutIndeterminateMemberCount:
      input.matrixPlan
        .layoutIndeterminateMemberCount,

    sourceMutationAllowed:
      false,

    causalAttributionAllowed:
      false,

    repairAuthorized:
      false,

    legacyRetirementAuthorized:
      false,

    productionAuthorityChanged:
      false,

    privateIdentityPublished:
      false,

    readOnly:
      true,

    repairExecuted:
      false,
  });
};
